import React, { useState, useEffect } from 'react';
import { FaMapMarkerAlt, FaBriefcase, FaEye, FaClock, FaUser, FaCrown, FaCheckCircle } from 'react-icons/fa';
import api from '../../services/api';

const Visitors = ({ setActiveTab }) => {
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [connectFeedback, setConnectFeedback] = useState({});

    // Helper to get correct image URL
    const getProfileImage = (img, gender) => {
        if (!img) {
            return gender === 'Female'
                ? 'https://randomuser.me/api/portraits/women/85.jpg'
                : 'https://randomuser.me/api/portraits/men/85.jpg';
        }

        if (img.startsWith('http') || img.startsWith('data:') || img.startsWith('blob:')) {
            return img;
        }

        const BASE_URL = api.defaults.baseURL;
        const baseUrl = BASE_URL.replace("/api", "");

        const cleanPath = img.startsWith('/') ? img : `/${img}`;
        return `${baseUrl}${cleanPath}`;
    };
    const validVisitors = profiles.filter(
        (p) => p._id || p.id || p.name || p.userId
    );

    useEffect(() => {
        const fetchVisitors = async () => {
            try {
                const response = await api.get('/visitors');
                setProfiles(response.data || []);
            } catch (error) {
                console.error('Error fetching visitors:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchVisitors();
    }, []);
    const hasActiveSubscription = profiles.some(
        (p) => p.hasSubscription === true
    );

    const handleConnect = (profileId, profileName) => {
        setConnectFeedback(prev => ({ ...prev, [profileId]: true }));

        setTimeout(() => {
            setConnectFeedback(prev => ({ ...prev, [profileId]: false }));
        }, 2000);
    };
    

    const handleUpgradeClick = () => {
        if (setActiveTab) {
            setActiveTab('subscription');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading visitors...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-[1400px] mx-auto px-4 py-8">
            {/* Header Section */}
            <div className="mb-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Profile Visitors</h1>
                        <p className="text-gray-600">
                            People who viewed your profile
                            <span className="font-semibold text-rose-600 ml-1">
                                {validVisitors.length} visitors
                            </span>
                        </p>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-rose-600">
                                {validVisitors.length}
                            </div>
                            <div className="text-sm text-gray-500">Total Views</div>
                        </div>
                        <div className="h-8 w-px bg-gray-200"></div>
                        <div className="text-center">
                            {/* <div className="text-2xl font-bold text-gray-900">
                                {profiles.length > 0 ?
                                    Math.round(profiles.reduce((sum, p) => sum + (p.age || 30), 0) / profiles.length)
                                    : 0}
                            </div>
                            <div className="text-sm text-gray-500">Avg Age</div> */}
                        </div>
                    </div>
                </div>
            </div>

            {/* Upgrade Banner */}
            {!hasActiveSubscription && (
                <div className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-lg border border-rose-100 p-6 mb-8">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="flex items-start gap-4">
                            <div className="p-3 bg-rose-100 rounded-lg">
                                <FaCrown className="text-rose-600 text-xl" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-rose-900 mb-1">
                                    Get more profile visitors!
                                </h3>
                                <p className="text-sm text-rose-700">
                                    Premium members get 10x more profile views and better match recommendations.
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleUpgradeClick}
                            className="px-6 py-3 bg-rose-600 text-white rounded-lg font-medium hover:bg-rose-700 transition-colors shadow-md whitespace-nowrap"
                        >
                            Upgrade to Premium
                        </button>
                    </div>
                </div>
            )}


            {/* Visitors List */}
            <div className="space-y-4">
                {validVisitors.length === 0 ? (
                    <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                        <div className="text-5xl mb-4 text-gray-300">👁️</div>
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">No visitors yet</h3>
                        <p className="text-gray-500 max-w-md mx-auto mb-6">
                            Your profile hasn't been viewed by anyone yet. Complete your profile to get more visibility.
                        </p>
                        <button
                            onClick={handleUpgradeClick}
                            className="px-6 py-3 bg-rose-600 text-white rounded-lg font-medium hover:bg-rose-700 transition-colors"
                        >
                            Boost Profile Visibility
                        </button>
                    </div>
                ) : (
                    validVisitors.map(profile => {
                        const gender = profile.gender || 'Male';
                        const isConnected = connectFeedback[profile.id || profile._id];

                        return (
                            <div
                                key={profile.id || profile._id}
                                className="bg-white rounded-lg border border-gray-200 hover:border-rose-200 hover:shadow-md transition-all duration-300 p-4 flex items-center gap-4 group"
                            >
                                {/* Profile Image */}
                                <div className="relative">
                                    <img
                                        src={getProfileImage(profile.image, gender)}
                                        alt={profile.name}
                                        onError={(e) => {
                                            e.currentTarget.onerror = null;
                                            e.currentTarget.src = gender === 'Female'
                                                ? 'https://randomuser.me/api/portraits/women/85.jpg'
                                                : 'https://randomuser.me/api/portraits/men/85.jpg';
                                        }}
                                        className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-sm"
                                    />
                                    {profile.isVerified && (
                                        <div className="absolute -bottom-1 -right-1 bg-blue-500 text-white p-1 rounded-full border-2 border-white">
                                            <FaCheckCircle size={12} />
                                        </div>
                                    )}
                                </div>

                                {/* Profile Info */}
                                <div className="flex-1">
                                    <div className="flex flex-col md:flex-row md:items-center gap-2 mb-2">
                                        <h3 className="font-bold text-gray-900 flex items-center gap-2">
                                            {profile.name || 'Unknown'}
                                            {profile.isPremium && (
                                                <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                                                    <FaCrown size={10} className="inline mr-1" /> Premium
                                                </span>
                                            )}
                                        </h3>
                                        <div className="flex items-center gap-3">
                                            {profile.age && (
                                                <span className="text-sm text-gray-600">{profile.age} years</span>
                                            )}
                                            {profile.time && (
                                                <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded-full flex items-center gap-1">
                                                    <FaClock size={10} /> {profile.time}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                                        {profile.location && (
                                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                                <FaMapMarkerAlt className="text-rose-500" size={14} />
                                                <span>{profile.location}</span>
                                            </div>
                                        )}
                                        {profile.job && (
                                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                                <FaBriefcase className="text-rose-500" size={14} />
                                                <span>{profile.job}</span>
                                            </div>
                                        )}
                                        {profile.viewCount && profile.viewCount > 1 && (
                                            <div className="text-xs text-rose-600 bg-rose-50 px-2 py-1 rounded-full">
                                                Viewed {profile.viewCount} times
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Action Button */}

                            </div>
                        );
                    })
                )}
            </div>

            {/* View Stats */}
            {profiles.length > 0 && (
                <div className="mt-8 bg-gray-50 rounded-lg border border-gray-200 p-6">
                    <div className="flex items-start gap-4">
                        <div className="p-2 bg-rose-100 rounded-lg">
                            <FaEye className="text-rose-600 text-xl" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Profile Visibility Insights</h3>
                            <p className="text-gray-600 text-sm">
                                Your profile has been viewed {profiles.length} times in the last 30 days.
                                Completing your profile details can increase visibility by up to 300%.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Visitors;